/* eslint-disable react-hooks/exhaustive-deps */
/* eslint-disable @typescript-eslint/no-unused-vars */
import { useEffect, useState } from "react";
import {
  IconDownload,
  IconList,
  IconLayoutGrid,
  IconEye,
} from "@tabler/icons-react";
import { useRolesData, useStaffsData } from "../../apis/staffApis";
import { useNavigate } from "react-router-dom";
import ToggleButton from "../../components/ToggleButton";
import ListView from "../../components/common/ListView";
import GridView from "../../components/common/GridView";
import NormalSearch from "../../components/common/NormalSearch";
import useDebounce from "../../utilities/useDebounce";
import Pagination from "../../components/common/Pagination";
import TableSkeleton from "../../components/skeleton/TableSkeleton";
import EntriesSelector from "../../components/common/EntriesSelector";
import { handleDownload } from "../../utilities/downloadHelper";
import { formatDateTime } from "../../utilities/helper";
import ColumnsFilter from "../../components/common/ColumnsFilter";
import NoDataFound from "../../components/common/NoDataFound";
import { usePermission } from "../../hooks/usePermissionTabs";

type StaffData = {
  id: string;
  roles: string;
  fullName: string;
  phoneNumber: string;
  email: string;
  country: string;
  state: string;
  city: string;
  iqamaNo: string;
  status: string;
  roleName: string;
  roleId: number;
  doc: string;
  countryId: string;
  stateId: string;
  createdAt: string;
};

export default function StaffManagement() {
  const [viewMode, setViewMode] = useState<string>(
    () => localStorage.getItem("StaffModeActiveRole") || "list",
  );

  useEffect(() => {
    localStorage.setItem("StaffModeActiveRole", viewMode);
  }, [viewMode]);

  const [searchFor, setSearchFor] = useState<string>("");
  const [pageNo, setPageNo] = useState<number>(1);
  const [limit, setLimit] = useState<any>(10);
  const [id, setId] = useState<string>("");
  const [status, setStatus] = useState<string>("");
  const [selectedRows, setSelectedRows] = useState<string[]>([]);
  const [allSelectedData, setAllSelectedData] = useState<StaffData[]>([]);
  const [showCheckboxes, setShowCheckboxes] = useState<boolean>(false);
  const [activeModal, setActiveModal] = useState<boolean>(false);

  const {
    data,
    isPending,
    refetch: fetchData,
  } = useStaffsData(pageNo, limit, searchFor);

  const debouncedSearchFor = useDebounce(searchFor, 550);
  const navigate = useNavigate();

  const { allowed: canRead, isLoading: permLoading } = usePermission(
    "staffs",
    "read",
  );

  useEffect(() => {
    fetchData();
  }, [pageNo, limit, debouncedSearchFor]);

  useEffect(() => {
    if (data?.staffs) {
      setAllSelectedData((prev) => {
        const filteredPrev = prev.filter((item) =>
          selectedRows.includes(item.id),
        );
        const newSelected = data.staffs.filter(
          (s: any) =>
            selectedRows.includes(s.id) &&
            !filteredPrev.some((item) => item.id === s.id),
        );
        return [...filteredPrev, ...newSelected];
      });
    }
  }, [data?.staffs, selectedRows]);

  const handlePaginationFetch = (page: number) => setPageNo(page);

  const handleSelectRow = (rowId: string) => {
    setSelectedRows((prev) =>
      prev.includes(rowId) ? prev.filter((r) => r !== rowId) : [...prev, rowId],
    );
  };

  const handleSelectAll = () => {
    if (selectedRows.length === data?.staffs?.length) {
      setSelectedRows((prev) =>
        prev.filter((id) => !data.staffs.some((s: any) => s.id === id)),
      );
    } else {
      const newSelections =
        data?.staffs
          ?.filter((s: any) => !selectedRows.includes(s.id))
          .map((s: any) => s.id) || [];
      setSelectedRows((prev) => [...prev, ...newSelections]);
    }
  };

  const [columnsData, setColumnsData] = useState([
    { text: "Checkbox", value: "checkbox", isShow: showCheckboxes },
    { text: "S/N", value: "sn", isShow: true },
    { text: "Name", value: "name", isShow: true },
    { text: "Staff Role", value: "staffRole", isShow: true },
    { text: "Mobile No", value: "mobileNo", isShow: true },
    { text: "Email", value: "email", isShow: true },
    { text: "Date", value: "createdAt", isShow: true },
    { text: "Status", value: "status", isShow: true },
    { text: "Actions", value: "actions", isShow: true },
  ]);

  const setHeadings = (value: string | undefined, checked: boolean) => {
    setColumnsData((prev) =>
      prev.map((col) =>
        col.value === value ? { ...col, isShow: checked } : col,
      ),
    );
  };

  const columns = columnsData
    .filter((c) => c.isShow)
    .map((col) => {
      switch (col.value) {
        case "checkbox":
          return {
            label: (
              <label className="relative flex items-center cursor-pointer">
                <input
                  type="checkbox"
                  checked={
                    data?.staffs?.length > 0 &&
                    data?.staffs?.every((s: any) => selectedRows.includes(s.id))
                  }
                  onChange={handleSelectAll}
                  className="peer h-5 w-5 cursor-pointer appearance-none rounded border border-slate-300 checked:border-[#FBD037] bg-white checked:bg-[#FBD037]"
                />
              </label>
            ),
            render: (s: any) => (
              <label className="relative flex items-center cursor-pointer">
                <input
                  type="checkbox"
                  checked={selectedRows.includes(s.id)}
                  onChange={() => handleSelectRow(s.id)}
                  className="peer h-5 w-5 cursor-pointer appearance-none rounded border border-slate-300 checked:border-[#FBD037] bg-white checked:bg-[#FBD037]"
                />
              </label>
            ),
          };
        case "sn":
          return {
            label: "#",
            render: (_: any, i: number) => (pageNo - 1) * limit + (i + 1),
          };
        case "name":
          return {
            label: "Name",
            render: (s: any) => (
              <div className="flex items-center gap-2 truncate max-w-xs">
                <img
                  src={s?.photo || "/img/dummy-blank-profile.png"}
                  className="w-10 h-10 rounded-full border object-cover"
                  alt={s.name}
                />
                <span className="truncate">{s.name}</span>
              </div>
            ),
          };
        case "staffRole":
          return { label: "Staff Role", render: (s: any) => s.roleName };
        case "mobileNo":
          return { label: "Mobile No", render: (s: any) => s.phone };
        case "email":
          return { label: "Email", render: (s: any) => s.email };
        case "createdAt":
          return {
            label: "Date",
            render: (s: any) => formatDateTime(s.created_at),
          };
        case "status":
          return {
            label: "Status",
            render: (s: any) => (
              <div className="flex items-center justify-center">
                <ToggleButton
                  onChange={() => {
                    setId(s?.id);
                    setStatus(s?.status);
                    setActiveModal(true);
                  }}
                  checked={s?.status === "ACTIVE"}
                />
              </div>
            ),
          };
        case "actions":
          return {
            label: "Actions",
            render: (s: any) => (
              <div className="flex items-center gap-2 justify-center">
                <button
                  onClick={() => navigate(`/staff/${s.id}`)}
                  className="p-2 border rounded-full bg-[#F7F6F2] hover:bg-[#FBD037]/20 transition-colors"
                >
                  <IconEye size={22} />
                </button>
              </div>
            ),
          };
        default:
          return { label: "", render: () => null };
      }
    });

  const mapDataToExport = (s: any, index: number) => ({
    "S/N": index + 1,
    Name: s.name || s.fullName,
    "Staff Role": s.roleName,
    "Mobile No": s.phone || s.phoneNumber,
    Email: s.email,
    Date: formatDateTime(s.created_at),
    Status: s.status,
  });

  if (permLoading) {
    return (
      <div className="mx-6 animate-pulse space-y-3">
        <div className="bg-[#F7F6F2] flex items-center justify-between px-6 py-3 rounded-tl-3xl rounded-tr-[40px]">
          <div className="h-6 w-40 bg-gray-200 rounded-full" />
          <div className="flex gap-2">
            <div className="h-8 w-24 bg-gray-200 rounded-full" />
            <div className="h-8 w-8 bg-gray-200 rounded-full" />
          </div>
        </div>
        <TableSkeleton
          columns={columnsData.filter((c) => c.isShow).map((c) => c.text)}
          rows={6}
        />
      </div>
    );
  }

  return (
    <div className="mx-6">
      {/* ── Header ─────────────────────────────────────────────────────── */}
      <div className="flex items-center">
        <div className="bg-[#F7F6F2] flex items-center justify-between px-6 py-3 w-full rounded-tl-3xl rounded-tr-[40px]">
          <h1 className="text-xl text-[#212121] tracking-[-0.5px] font-medium">
            Staff Management
          </h1>
          <div className="flex items-center gap-2">
            {data && data.totalPages * limit > 10 && (
              <EntriesSelector
                value={limit}
                options={[
                  10,
                  ...(data.totalPages * limit > 10 ? [20] : []),
                  ...(data.totalPages * limit > 20 ? [30] : []),
                  ...(data.totalPages * limit > 50 ? [50] : []),
                  ...(data.totalPages * limit > 100 ? [100] : []),
                ]}
                onChange={(val) => {
                  setLimit(val);
                  setPageNo(1);
                  setSelectedRows([]);
                  setAllSelectedData([]);
                }}
              />
            )}
            <ColumnsFilter
              columnsData={columnsData.filter((c) => c.value !== "checkbox")}
              setHeadings={setHeadings}
            />
            <NormalSearch
              value={searchFor}
              onChange={(e) => {
                setSearchFor(e.target.value);
                setPageNo(1);
                setSelectedRows([]);
                setAllSelectedData([]);
              }}
            />
          </div>
        </div>

        <div className="relative ml-5 flex items-center gap-2 justify-end rounded-full">
          <div className="flex border border-[#FFFFFF] bg-[#F7F6F2] p-0.5 rounded-full">
            <button
              onClick={() => setViewMode("list")}
              className={`p-2 rounded-l-full transition-colors ${
                viewMode === "list" ? "bg-[#FBD037]/50" : ""
              }`}
            >
              <IconList stroke={1.5} size={24} />
            </button>
            <button
              onClick={() => setViewMode("grid")}
              className={`p-2 rounded-r-full transition-colors ${
                viewMode === "grid" ? "bg-[#FBD037]/50" : ""
              }`}
            >
              <IconLayoutGrid stroke={1.5} size={20} />
            </button>
          </div>

          <button
            onClick={() =>
              handleDownload({
                selectedRows,
                allSelectedData,
                setSelectedRows,
                setAllSelectedData,
                showCheckboxes,
                setShowCheckboxes,
                setColumnsData,
                mapDataToExport,
                fileName: "staff_management.xlsx",
              })
            }
            className={`flex items-center gap-1 p-2.5 text-sm rounded-full border border-[#FFFFFF] transition-colors ${
              showCheckboxes && selectedRows.length === 0
                ? "bg-[#F7F6F2]/50 text-[#404040]/50"
                : "bg-[#F7F6F2] text-[#404040] hover:bg-[#FBD037]/20"
            }`}
          >
            <IconDownload stroke={1.5} size={24} />
            {selectedRows.length > 0 && <span>({selectedRows.length})</span>}
          </button>
        </div>
      </div>

      {/* ── Content ─────────────────────────────────────────────────────── */}
      <div>
        {viewMode === "list" ? (
          <>
            {isPending ? (
              <TableSkeleton
                columns={columnsData.filter((c) => c.isShow).map((c) => c.text)}
                rows={6}
              />
            ) : data?.staffs?.length ? (
              <ListView
                data={data.staffs}
                onRowClick={(staff) => console.log("Clicked:", staff)}
                columns={columns}
              />
            ) : (
              <NoDataFound />
            )}
          </>
        ) : isPending ? (
          <TableSkeleton columns={[]} rows={6} />
        ) : data?.staffs?.length ? (
          <GridView
            columns={5}
            className="p-2 grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 2xl:grid-cols-5 bg-[#F7F6F2] rounded-tr-3xl rounded-b-3xl"
            data={data.staffs}
            renderItem={(staff: any) => (
              <div className="rounded-2xl p-4 transition-all bg-[#FCFDF8] scale-100 hover:scale-105 space-y-4">
                <div className="flex justify-between items-center">
                  <button
                    onClick={(e) => {
                      e.stopPropagation();
                      setId(staff?.id);
                      setStatus(staff?.status);
                      setActiveModal(true);
                    }}
                    className={`text-sm px-4 py-1 border rounded-lg capitalize cursor-pointer ${
                      staff.status === "ACTIVE"
                        ? "bg-[#20BF55]/20 border-[#20BF55]"
                        : "bg-red-500/20 border-red-500"
                    }`}
                  >
                    {staff?.status?.toLowerCase()}
                  </button>
                  <button
                    onClick={() => navigate(`/staff/${staff.id}`)}
                    className="p-1 border rounded-full bg-[#F7F6F2] hover:bg-[#FBD037]/20 transition-colors"
                  >
                    <IconEye size={22} />
                  </button>
                </div>
                <div className="flex items-center flex-col w-full gap-2 justify-center">
                  <img
                    src={staff?.photo || "/img/dummy-blank-profile.png"}
                    className="size-20 rounded-full object-cover border"
                    alt={staff.name}
                  />
                  <div className="w-full">
                    <h2 className="text-[#212121] text-center 2xl:text-xl text-lg font-medium line-clamp-1">
                      {staff.name}
                    </h2>
                    <p className="text-[#212121]/80 text-center text-sm">
                      {staff.roleName}
                    </p>
                  </div>
                </div>
              </div>
            )}
          />
        ) : (
          <NoDataFound />
        )}

        <div className="py-4 flex justify-center">
          {data?.totalPages > 1 && (
            <Pagination
              currentPage={pageNo}
              totalPages={data.totalPages}
              fetchData={handlePaginationFetch}
            />
          )}
        </div>
      </div>
    </div>
  );
}
